import { useMemo } from "react";
import { useAuth } from "@/context/AuthContext.jsx";
import { useHybridDataStore } from "@/hooks/useHybridDataStore.js";
import { getDemoContextForAccount } from "@/data/dojoOsOrgModel.js";
import { getStoredBackendUser } from "@/api/karateBackendApi.js";
import { findMembershipForUsername, getMembershipById } from "@/membership/membershipStore.js";

export function useRoleHybridContext() {
  const { username, user } = useAuth();
  const { store, backendStatus, isHybrid } = useHybridDataStore();
  const backendUser = getStoredBackendUser();
  const demoCtx = getDemoContextForAccount(username);

  return useMemo(() => {
    const fromBackend = store._dataSource === "backend" && backendStatus === "backend";
    const membershipFound = findMembershipForUsername(username);
    const membershipDojoId = membershipFound.credential?.dojoId || membershipFound.membership?.identity?.dojoId;
    const membershipStudentId = membershipFound.credential?.studentId || membershipFound.membership?.studentId;
    const parentChildStudentIds = (membershipFound.credential?.linkedMembershipIds || [])
      .map((id) => getMembershipById(id)?.studentId)
      .filter(Boolean);

    const dojoIds = backendUser?.dojoIds?.length
      ? backendUser.dojoIds
      : demoCtx?.dojoId
        ? [demoCtx.dojoId]
        : membershipDojoId
          ? [membershipDojoId]
          : [];

    const dojo = dojoIds[0] ? store.dojos.find((d) => d.id === dojoIds[0]) : null;
    const students = dojoIds.length
      ? store.etudiants.filter((e) => dojoIds.includes(e.dojo_id))
      : [];

    const childStudentIds = backendUser?.childStudentIds?.length
      ? backendUser.childStudentIds
      : demoCtx?.childStudentIds?.length
        ? demoCtx.childStudentIds
        : parentChildStudentIds;

    const children = childStudentIds.length
      ? store.etudiants.filter((e) => childStudentIds.includes(e.id))
      : [];

    const ownStudentId = backendUser?.studentId || demoCtx?.studentId || membershipStudentId;
    const ownStudent = ownStudentId ? store.etudiants.find((e) => e.id === ownStudentId) : null;

    return {
      fromBackend,
      isHybrid,
      dojoIds,
      dojo,
      students,
      children,
      ownStudent,
      hasDojo: Boolean(dojo),
      hasChildren: children.length > 0,
      hasProfile: Boolean(ownStudent || backendUser?.person),
      backendUser,
      demoCtx,
    };
  }, [store, backendStatus, backendUser, demoCtx, username, user, isHybrid]);
}
